import type { JSX } from "./jsx-runtime.d.ts";

// animations/
import scroll from "./animations/scroll.js";
import slideDown from "./animations/slideDown.js";
import slideUp from "./animations/slideUp.js";

// config/
import { getAnimationMode, setAnimationMode } from "./config/AnimationMode.js";
import { getCalendarType } from "./config/CalendarType.js";
import { getFirstDayOfWeek, getLegacyDateCalendarCustomizing } from "./config/FormatSettings.js";
import {
	setDefaultIconCollection,
	getDefaultIconCollection,
} from "./config/Icons.js";
import { RegisteredIconCollection } from "./asset-registries/util/IconCollectionsByTheme.js";
import getEffectiveIconCollection from "./asset-registries/util/getIconCollectionByTheme.js";
import {
	getLanguage,
	setLanguage,
	getDefaultLanguage,
	setFetchDefaultLanguage,
	getFetchDefaultLanguage,
} from "./config/Language.js";
import { getNoConflict, setNoConflict } from "./config/NoConflict.js";
import {
	getTheme,
	setTheme,
	getDefaultTheme,
} from "./config/Theme.js";

// decorators/
import customElement from "./decorators/customElement.js";
import event from "./decorators/event.js";
import property from "./decorators/property.js";
import slot from "./decorators/slot.js";

// delegate/
import ItemNavigation from "./delegate/ItemNavigation.js";
import ResizeHandler from "./delegate/ResizeHandler.js";
import ScrollEnablement from "./delegate/ScrollEnablement.js";

// locale/
import applyDirection from "./locale/applyDirection.js";
import { attachDirectionChange, detachDirectionChange } from "./locale/directionChange.js";
import getEffectiveDir from "./locale/getEffectiveDir.js";
import { attachLanguageChange, detachLanguageChange } from "./locale/languageChange.js";

// util/
import { URLListValidator, sanitizeHTML } from "./util/HTMLSanitizer.js";

// Assets.ts
import { registerI18nLoader } from "./asset-registries/i18n.js";
import { registerLocaleDataLoader } from "./asset-registries/LocaleData.js";
import { registerThemePropertiesLoader } from "./asset-registries/Themes.js";
import { registerIconLoader } from "./asset-registries/Icons.js";

// Boot.ts
import { attachBoot } from "./Boot.js";

// CustomElementsScope.ts
import {
	setCustomElementsScopingSuffix,
	getCustomElementsScopingSuffix,
	setCustomElementsScopingRules,
	getCustomElementsScopingRules,
	getEffectiveScopingSuffixForTag,
} from "./CustomElementsScope.js";

// Device.ts
import {
	supportsTouch,
	isSafari,
	isChrome,
	isFirefox,
	isPhone,
	isTablet,
	isDesktop,
	isCombi,
	isIOS,
	isAndroid,
} from "./Device.js";

// EventProvider.ts
import EventProvider from "./EventProvider.js";

// i18nBundle.ts
import I18nBundle, { getI18nBundle, registerCustomI18nBundleGetter } from "./i18nBundle.js";

// MediaRange.ts
import MediaRange from "./MediaRange.js";

// Render.ts
import {
	renderDeferred,
	renderImmediately,
	cancelRender,
	renderFinished,
} from "./Render.js";

// Theming.ts
import { addCustomCSS, attachThemeLoaded, detachThemeLoaded } from "./Theming.js";

// UI5Element.ts
import UI5Element from "./UI5Element.js";

export default UI5Element;
export {
	// animations/
	scroll,
	slideDown,
	slideUp,

	// config/
	getAnimationMode,
	setAnimationMode,
	getCalendarType,
	getFirstDayOfWeek,
	getLegacyDateCalendarCustomizing,
	setDefaultIconCollection,
	getDefaultIconCollection,
	getEffectiveIconCollection,
	RegisteredIconCollection,
	getLanguage,
	setLanguage,
	getDefaultLanguage,
	setFetchDefaultLanguage,
	getFetchDefaultLanguage,
	getNoConflict,
	setNoConflict,
	getTheme,
	setTheme,
	getDefaultTheme,

	// decorators/
	customElement,
	event,
	property,
	slot,

	// delegate/
	ItemNavigation,
	ResizeHandler,
	ScrollEnablement,

	// locale/
	applyDirection,
	attachDirectionChange,
	detachDirectionChange,
	getEffectiveDir,
	attachLanguageChange,
	detachLanguageChange,

	// util/
	URLListValidator,
	sanitizeHTML,

	// Assets.ts
	registerI18nLoader,
	registerLocaleDataLoader,
	registerThemePropertiesLoader,
	registerIconLoader,

	// Boot.ts
	attachBoot,

	// CustomElementsScope.ts
	setCustomElementsScopingSuffix,
	getCustomElementsScopingSuffix,
	setCustomElementsScopingRules,
	getCustomElementsScopingRules,
	getEffectiveScopingSuffixForTag,

	// Device.ts
	supportsTouch,
	isSafari,
	isChrome,
	isFirefox,
	isPhone,
	isTablet,
	isDesktop,
	isCombi,
	isIOS,
	isAndroid,

	// EventProvider.ts
	EventProvider,

	// i18nBundle.ts
	I18nBundle,
	getI18nBundle,
	registerCustomI18nBundleGetter,

	// MediaRange.ts
	MediaRange,

	// Render.ts
	renderDeferred,
	renderImmediately,
	cancelRender,
	renderFinished,

	// Theming.ts
	addCustomCSS,
	attachThemeLoaded,
	detachThemeLoaded,

	// UI5Element.ts
	UI5Element,
};

type TargetedCustomEvent<D, T> = Omit<CustomEvent<D>, "currentTarget"> & { currentTarget: T };
// export type UI5NativeEvent<T extends keyof HTMLElementTagNameMap, N> = Parameters<JSX.IntrinsicElements[T][N]>[0];
export type UI5CustomEvent<T extends UI5Element, N extends keyof T["eventDetails"]> = TargetedCustomEvent<T["eventDetails"][N], T>;
export type JsxTemplateResult = JSX.Element | void;
export type JsxTemplate = () => JsxTemplateResult;

export type * from "./types.d.ts";
export type * from "./jsx-runtime.d.ts";
